import { Component, OnInit, OnDestroy, Inject, HostBinding } from '@angular/core';
import { Subscription } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DirectionService } from '../../direction.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { DataSharingService } from '../../main-section/data-shering-service/data-sharing.service';
import { AuthService } from '../../auth/auth.service';
import { UsersService } from 'src/app/super-management/user/users.service';
import { DialogService } from '../dialog.service';
import { trigger, state, style, transition, animate } from '@angular/animations';

export type PhPrintExplanationMode = '3d' | 'phPrint';

export interface PhPrintExplanationDialogData {
  mode: PhPrintExplanationMode;
}

@Component({
  selector: 'app-ph-print-explanation',
  templateUrl: './ph-3d-print-explanation.component.html',
  styleUrls: ['./ph-3d-print-explanation.component.css'],
  host: {
    class: 'fill-screen-modal-ph-print-explanation'
  },
  animations: [
    trigger('slideDownUp', [
      state('void', style({
        maxHeight: '0',
        opacity: 0,
        marginTop: '0',
        overflow: 'hidden'
      })),
      state('*', style({
        maxHeight: '500px',
        opacity: 1,
        marginTop: '0.5rem',
        overflow: 'visible'
      })),
      transition('void => *', [
        animate('0.3s ease-out')
      ]),
      transition('* => void', [
        animate('0.3s ease-in')
      ])
    ])
  ]
})
export class PhPrintExplanationComponent implements OnInit, OnDestroy {
  isRTL: boolean;
  isDarkMode: boolean = false;
  isScreenWide: boolean = false;
  selectedTechnology: string | null = null;
  isLoading: boolean = false;
  private directionSubscription: Subscription;
  private resizeListener?: () => void;

  /** Steps 2–5 in PH (non‑3D) flow: skip legacy step 4 (quote); renumber 5→4, 6→5 */
  readonly phPrintFlowSteps: ReadonlyArray<{ stepLabel: number; titleKey: string; contentKey: string }> = [
    { stepLabel: 2, titleKey: 'tree-d-print.explain.2.title', contentKey: 'ph-print-explain.flow.step2.content' },
    { stepLabel: 3, titleKey: 'tree-d-print.explain.3.title', contentKey: 'tree-d-print.explain.3.content' },
    { stepLabel: 4, titleKey: 'tree-d-print.explain.5.title', contentKey: 'tree-d-print.explain.5.content' },
    { stepLabel: 5, titleKey: 'tree-d-print.explain.6.title', contentKey: 'tree-d-print.explain.6.content' },
  ];

  @HostBinding('attr.dir') get direction(): string {
    return this.isRTL ? 'rtl' : 'ltr';
  }

  linksBanck3DPrint = [
    { url: 'https://makerworld.com/en' },
    { url: 'https://www.thingiverse.com/' },
    { url: 'https://www.myminifactory.com/' },
    { url: 'https://cults3d.com/' },
    { url: 'https://grabcad.com/' }
  ];

  constructor(
    private directionService: DirectionService,
    public dialogRef: MatDialogRef<PhPrintExplanationComponent>,
    private translateService: TranslateService,
    private router: Router,
    private dataSharingService: DataSharingService,
    private authService: AuthService,
    private usersService: UsersService,
    private dialogService: DialogService,
    @Inject(MAT_DIALOG_DATA) public data: PhPrintExplanationDialogData
  ) {
    const currentDir = document.documentElement.getAttribute('dir');
    this.isRTL = currentDir === 'rtl';
  }

  get isPhPrintMode(): boolean {
    return this.data?.mode === 'phPrint';
  }

  ngOnInit() {
    this.directionSubscription = this.directionService.direction$.subscribe(direction => {
      this.isRTL = direction === 'rtl';
    });

    this.directionService.isDarkMode$.subscribe(isDarkMode => {
      this.isDarkMode = isDarkMode;
    });

    this.isScreenWide = window.innerWidth >= 768;
    this.resizeListener = () => {
      this.isScreenWide = window.innerWidth >= 768;
    };
    window.addEventListener('resize', this.resizeListener);
  }

  ngOnDestroy() {
    if (this.directionSubscription) {
      this.directionSubscription.unsubscribe();
    }
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  openBankLink(url: string) {
    if (url) {
      window.open(url, '_blank');
    }
  }

  onTechnologyClick(technology: string) {
    if (this.selectedTechnology === technology) {
      this.selectedTechnology = null;
    } else {
      this.selectedTechnology = technology;
    }
  }

  closeTechnologyExplanation() {
    this.selectedTechnology = null;
  }

  isTechnologyOpen(technology: string): boolean {
    return this.selectedTechnology === technology;
  }

  parentheticalExpansionDir(): 'ltr' | 'rtl' {
    const lang = this.translateService.currentLang || this.translateService.defaultLang;
    return lang === 'ar' ? 'rtl' : 'ltr';
  }

  async onUploadFileForTechnology(technology: string) {
    if (!technology) return;

    this.isLoading = true;
    this.dataSharingService.setProduct(technology);
    this.dataSharingService.setBranch(technology);

    const userIsAuthenticated = this.authService.getIsAuth();

    if (userIsAuthenticated) {
      await this.dataSharingService.setPrintingService("ph");
      await this.dataSharingService.setBranch(technology);
      this.authService.updateAuthData("ph", technology);
      this.usersService.updateUserPlace("ph", technology).subscribe(response => {
        this.dialogRef.close();
        this.router.navigate(['/phprint']);
      });
    } else {
      this.dialogRef.close();
      this.dialogService.onOpenLoginDialog('ph', '');
    }
  }
}
